/* Generated from Java with JSweet 2.3.7 - http://www.jsweet.org */
import { LoggerCompat } from '../../compat/LoggerCompat';
import { Document } from '../../core/models/Document';

/**
 * Constructor.
 * @param {Document} document
 * @class
 * @author eric.wittmann@gmail.com
 */
export class OtEngine {
    /*private*/ pendingCommands : Array<OtCommand>;

    /*private*/ commands : Array<OtCommand>;

    /*private*/ document : Document;

    /*private*/ pendingUndos : Array<number>;

    public constructor(document : Document) {
        if(this.pendingCommands===undefined) this.pendingCommands = null;
        if(this.commands===undefined) this.commands = null;
        if(this.document===undefined) this.document = null;
        if(this.pendingUndos===undefined) this.pendingUndos = null;
        this.document = document;
        this.pendingCommands = <any>([]);
        this.pendingUndos = <any>([]);
        this.commands = <any>([]);
    }

    /**
     * Gets the current document.
     * @return {Document} {OasDocument}
     */
    public getCurrentDocument() : Document {
        return this.document;
    }

    /**
     * Returns true if there is at least one pending command in the engine.  A pending command is one
     * that has not yet been finalized.  This typically means that the command has been applied to
     * the local document but not persisted in some remote store.
     * @return {boolean} {boolean}
     */
    public hasPendingCommands() : boolean {
        return /* size */(<number>this.pendingCommands.length) > 0;
    }

    /**
     * Returns true if the OT engine has already seen the given command.
     * @param {OtCommand} command
     * @return {boolean}
     */
    public hasCommand(command : OtCommand) : boolean {
        for(let index218=0; index218 < this.commands.length; index218++) {
            let cmd = this.commands[index218];
            {
                if(cmd.contentVersion === command.contentVersion) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Executes the given command in the correct sequence.  This command must have a valid
     * finalized contentVersion property.  This property will determine where in the sequence
     * of commands this one falls.  The engine will revert the document to an appropriate state
     * so that the command can be executed in the correct order.  During this process, existing
     * commands may need to be undone and then re-executed after.
     * 
     * Here's what happens when executing a command:
     * 
     * 1) "undo" all pending commands, since those are always executed last
     * 2) identify the insertion point of the command in the finalized command queue
     * 3) "undo" all finalized commands that fall AFTER this command in the finalized command queue
     * 4) execute this command and insert it into the finalized command queue
     * 5) re-execute all finalized commands that were undone in step #3
     * 6) re-execute all pending commands
     * 7) profit!
     * 
     * A future optimization of this algorithm is to only undo/redo the commands that conflict
     * with this command.  This will avoid unnecessary work (why bother undoing/redoing when
     * there is no potential for a conflict in the document).  This optimization can be achieved
     * by ensuring that each command has a list of NodePaths that represent the affected parts of
     * the document tree.  These paths can be used to determine which commands conflict with
     * other commands.
     * 
     * Whenever a command is executed, any local commands that have been undone can now be
     * safely removed from the list of commands, because making a change (by definition) orphans
     * any existing reverted commands.  There is no way to recover them.
     * 
     * @param {OtCommand} command
     * @param {boolean} pending
     */
    public executeCommand(command : OtCommand, pending : boolean) {
        if(pending) {
            command.local = true;
            LoggerCompat.info("[OtEngine] Executing PENDING command with contentId: %o", command.contentVersion);
            command.execute(this.document);
            /* add */(this.pendingCommands.push(command)>0);
            this.pruneRevertedCommands();
            return;
        }
        if(this.hasCommand(command)) {
            LoggerCompat.info("[OtEngine] Detected duplicate OtCommand with content version: %o", command.contentVersion);
            return;
        }
        LoggerCompat.info("[OtEngine] Executing command with content version: %o", command.contentVersion);
        let pidx : number;
        pidx = this.pendingUndos.indexOf(command.contentVersion);
        if(pidx !== -1) {
            /* remove */this.pendingUndos.splice(pidx, 1)[0];
            command.reverted = true;
        }
        for(pidx = /* size */(<number>this.pendingCommands.length) - 1; pidx >= 0; pidx--) {{
            if(!/* get */this.pendingCommands[pidx].reverted) {
                /* get */this.pendingCommands[pidx].command.undo(this.document);
            }
        };}
        let insertionIdx : number = /* size */(<number>this.commands.length) - 1;
        if(/* size */(<number>this.commands.length) > 0) {
            while((insertionIdx >= 0 && /* get */this.commands[insertionIdx].contentVersion > command.contentVersion)) {{
                if(!/* get */this.commands[insertionIdx].reverted) {
                    /* get */this.commands[insertionIdx].command.undo(this.document);
                }
                insertionIdx--;
            }};
        }
        insertionIdx++;
        /* add */this.commands.splice(insertionIdx, 0, command);
        let idx : number = insertionIdx;
        while((idx < /* size */(<number>this.commands.length))) {{
            /* get */this.commands[idx].execute(this.document);
            idx++;
        }};
        for(pidx = 0; pidx < /* size */(<number>this.pendingCommands.length); pidx++) {{
            /* get */this.pendingCommands[pidx].execute(this.document);
        };}
    }

    /**
     * Moves a commands from the "pending" queue to the "finalized" command queue.  This occurs
     * when a local (aka pending) command is acknowledged by the coordinating server and assigned
     * a final content version.  The engine must remove the command from the pending queue, update
     * its contentVersion, and then insert it at the correct location in the finalized queue.
     * 
     * Here's what happens when finalizing a command:
     * 
     * 1) "undo" all pending commands, shrinking the pending command queue to 0
     * 2) update the given pending command's contentVersion
     * 3) call "executeCommand()" with the newly finalized command
     * 4) re-execute all remaining pending commands
     * 
     * @param {number} pendingCommandId
     * @param {number} finalizedContentVersion
     */
    public finalizeCommand(pendingCommandId : number, finalizedContentVersion : number) {
        LoggerCompat.info("[OtEngine] Finalizing command with contentId: %o  and new contentVersion: %o", pendingCommandId, finalizedContentVersion);
        let isFirstPendingCmd : boolean = /* size */(<number>this.pendingCommands.length) > 0 && /* get */this.pendingCommands[0].contentVersion === pendingCommandId;
        let isLatestCmd : boolean = /* size */(<number>this.commands.length) === 0 || (/* get */this.commands[/* size */(<number>this.commands.length) - 1].contentVersion < finalizedContentVersion);
        if(isFirstPendingCmd && isLatestCmd) {
            LoggerCompat.info("[OtEngine] Pending command is \'next up\', performing simple shift from pending to finalized.");
            let command : OtCommand = /* remove */this.pendingCommands.splice(0, 1)[0];
            command.contentVersion = finalizedContentVersion;
            /* add */(this.commands.push(command)>0);
            return;
        }
        let pidx : number;
        for(pidx = /* size */(<number>this.pendingCommands.length) - 1; pidx >= 0; pidx--) {{
            if(!/* get */this.pendingCommands[pidx].reverted) {
                /* get */this.pendingCommands[pidx].command.undo(this.document);
            }
        };}
        let pending : Array<OtCommand> = this.pendingCommands;
        this.pendingCommands = <any>([]);
        let idx : number;
        let found : boolean = false;
        for(idx = 0; idx < /* size */(<number>pending.length); idx++) {{
            if(/* get */pending[idx].contentVersion === pendingCommandId) {
                found = true;
                break;
            }
        };}
        if(found) {
            let command : OtCommand = /* get */pending[idx];
            /* remove */pending.splice(idx, 1)[0];
            command.contentVersion = finalizedContentVersion;
            this.executeCommand(command, false);
        } else {
            LoggerCompat.info("[OtEngine] Attempted to finalize pending command %d but was not found.", pendingCommandId);
        }
        this.pendingCommands = pending;
        for(pidx = 0; pidx < /* size */(<number>this.pendingCommands.length); pidx++) {{
            /* get */this.pendingCommands[pidx].execute(this.document);
        };}
    }

    /**
     * Called to undo the last local command.  Returns the command that was undone (on success)
     * or null if there was no command to undo.
     * @return {OtCommand}
     */
    public undoLastLocalCommand() : OtCommand {
        let idx : number;
        for(idx = /* size */(<number>this.pendingCommands.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */this.pendingCommands[idx];
            if(!cmd.reverted) {
                cmd.undo(this.document);
                return cmd;
            }
        };}
        let undoneCmd : OtCommand = null;
        if(undoneCmd == null) {
            for(idx = /* size */(<number>this.commands.length) - 1; idx >= 0; idx--) {{
                let cmd : OtCommand = /* get */this.commands[idx];
                if(cmd.local && !cmd.reverted) {
                    undoneCmd = cmd;
                    break;
                }
            };}
        }
        if(undoneCmd != null) {
            this.undo(undoneCmd.contentVersion);
        }
        return undoneCmd;
    }

    /**
     * Called to redo the last "undone" local command.  Returns the command that was redone (on success)
     * or null if there was no command to redo.
     * @return {OtCommand}
     */
    public redoLastLocalCommand() : OtCommand {
        let idx : number;
        if(/* size */(<number>this.pendingCommands.length) > 0) {
            let cmd : OtCommand = /* get */this.pendingCommands[/* size */(<number>this.pendingCommands.length) - 1];
            if(cmd.reverted) {
                cmd.redo(this.document);
                return cmd;
            } else {
                return null;
            }
        }
        let redoneCmd : OtCommand = null;
        if(redoneCmd == null) {
            for(idx = /* size */(<number>this.commands.length) - 1; idx >= 0; idx--) {{
                let cmd : OtCommand = /* get */this.commands[idx];
                if(cmd.local && cmd.reverted) {
                    redoneCmd = cmd;
                }
                if(cmd.local && !cmd.reverted) {
                    break;
                }
            };}
        }
        if(redoneCmd != null) {
            this.redo(redoneCmd.contentVersion);
        }
        return redoneCmd;
    }

    /**
     * Called to undo a specific command by its contentVersion identifier.  Note: this will never
     * be invoked for a pending command (pending commands don't have content versions yet).
     * @param {number} contentVersion
     * @return {OtCommand}
     */
    public undo(contentVersion : number) : OtCommand {
        let idx : number;
        let commandsToUndo : Array<OtCommand> = <any>([]);
        LoggerCompat.info("[OtEngine] Undo command with content version: %o", contentVersion);
        for(idx = /* size */(<number>this.pendingCommands.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */this.pendingCommands[idx];
            /* add */(commandsToUndo.push(cmd)>0);
        };}
        let found : boolean = false;
        let foundCmd : OtCommand = null;
        for(idx = /* size */(<number>this.commands.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */this.commands[idx];
            /* add */(commandsToUndo.push(cmd)>0);
            if(cmd.contentVersion === contentVersion) {
                found = true;
                foundCmd = cmd;
                break;
            }
        };}
        if(!found) {
            /* add */(this.pendingUndos.push(contentVersion)>0);
            return null;
        }
        commandsToUndo.forEach((cmd) => {
            if(!cmd.reverted) {
                cmd.command.undo(this.document);
            }
        });
        foundCmd.reverted = true;
        for(idx = /* size */(<number>commandsToUndo.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */commandsToUndo[idx];
            cmd.execute(this.document);
        };}
        return foundCmd;
    }

    /**
     * Called to redo a specific command by its contentVersion identifier.
     * @param {number} contentVersion
     * @return {OtCommand}
     */
    public redo(contentVersion : number) : OtCommand {
        let idx : number;
        let commandsToUndo : Array<OtCommand> = <any>([]);
        LoggerCompat.info("[OtEngine] Redo command with content version: %o", contentVersion);
        for(idx = /* size */(<number>this.pendingCommands.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */this.pendingCommands[idx];
            /* add */(commandsToUndo.push(cmd)>0);
        };}
        let found : boolean = false;
        let foundCmd : OtCommand = null;
        for(idx = /* size */(<number>this.commands.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */this.commands[idx];
            /* add */(commandsToUndo.push(cmd)>0);
            if(cmd.contentVersion === contentVersion) {
                found = true;
                foundCmd = cmd;
                break;
            }
        };}
        if(!found) {
            idx = this.pendingUndos.indexOf(contentVersion);
            if(idx !== -1) {
                /* remove */this.pendingUndos.splice(idx, 1)[0];
            }
            return null;
        }
        commandsToUndo.forEach((cmd) => {
            if(!cmd.reverted) {
                cmd.command.undo(this.document);
            }
        });
        foundCmd.reverted = false;
        for(idx = /* size */(<number>commandsToUndo.length) - 1; idx >= 0; idx--) {{
            let cmd : OtCommand = /* get */commandsToUndo[idx];
            cmd.execute(this.document);
        };}
        return foundCmd;
    }

    /**
     * Called to remove any (local only) commands that have been undone/reverted.  This is
     * done whenever a new local command is executed.  It is done because when a new command
     * is executed, any existing reverted commands can no longer be "redone".
     */
    pruneRevertedCommands() {
        let cmds : Array<OtCommand> = <any>([]);
        this.commands.forEach(((cmds) => {
            return (cmd) => {
                if(cmd.reverted) /* add */(cmds.push(cmd)>0);
            }
        })(cmds));
        cmds.forEach((cmd) => /* remove */(a => { let index = a.indexOf(cmd); if(index>=0) { a.splice(index, 1); return true; } else { return false; }})(this.commands));
        if(!/* isEmpty */(cmds.length == 0)) {
            LoggerCompat.info("[OtEngine] Pruned %d reverted commands", /* size */(<number>cmds.length));
        }
        let pcmds : Array<OtCommand> = <any>([]);
        this.pendingCommands.forEach(((pcmds) => {
            return (cmd) => {
                if(cmd.reverted) /* add */(pcmds.push(cmd)>0);
            }
        })(pcmds));
        pcmds.forEach((cmd) => /* remove */(a => { let index = a.indexOf(cmd); if(index>=0) { a.splice(index, 1); return true; } else { return false; }})(this.pendingCommands));
        if(!/* isEmpty */(pcmds.length == 0)) {
            LoggerCompat.info("[OtEngine] Pruned %d reverted pending commands", /* size */(<number>pcmds.length));
        }
    }
}
OtEngine["__class"] = "io.apicurio.datamodels.cmd.ot.OtEngine";




import { OtCommand } from './OtCommand';

